import {
  FaCalendarAlt,
  FaUserFriends,
  FaMapMarkerAlt,
  FaBed,
} from "react-icons/fa";
import { useState, useContext, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import CalendarDropdown from "../ui/CalendarDropdown";
import { CurrencyContext } from "../common/CurrencyContext";

const cities = [
  { code: "GOA", name: "Goa (India)" },
  { code: "DXB", name: "Dubai" },
  { code: "DEL", name: "Delhi" },
  { code: "BOM", name: "Mumbai" },
  { code: "IST", name: "Istanbul" },
  { code: "LON", name: "London" },
];

const MAX_CHILD_AGE = 17;

interface HotelSearchbarProps {
  city?: string;
  checkInDate?: string | Date;
  checkOutDate?: string | Date;
  rooms?: number;
  guests?: number;
}

const HotelSearchbar = ({
  city: propCity,
  checkInDate: propCheckInDate,
  checkOutDate: propCheckOutDate,
  rooms: propRooms,
  guests: propGuests,
}: HotelSearchbarProps) => {
  const [selectedCity, setSelectedCity] = useState(
    cities.find((c) => c.code === propCity) || cities[0],
  );
  const [checkInDate, setCheckInDate] = useState<string | undefined>(
    typeof propCheckInDate === "string"
      ? propCheckInDate
      : propCheckInDate
        ? propCheckInDate.toISOString().split("T")[0]
        : undefined,
  );
  const [checkOutDate, setCheckOutDate] = useState<string | undefined>(
    typeof propCheckOutDate === "string"
      ? propCheckOutDate
      : propCheckOutDate
        ? propCheckOutDate.toISOString().split("T")[0]
        : undefined,
  );
  const [rooms, setRooms] = useState(propRooms ?? 1);
  const [adults, setAdults] = useState(propGuests ?? 2);
  const [children, setChildren] = useState(0);
  const [childAges, setChildAges] = useState<string>("");

  const [childAgeError, setChildAgeError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { currency } = useContext(CurrencyContext);

  const [placeQuery, setPlaceQuery] = useState("");
  const [placeSuggestions, setPlaceSuggestions] = useState<any[]>([]);
  const [selectedPlace, setSelectedPlace] = useState<any>(null);
  const [loadingSuggestions, setLoadingSuggestions] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const abortControllerRef = useRef<AbortController | null>(null);

  // Dropdown control state
  const [openDropdown, setOpenDropdown] = useState<
    null | "checkin" | "checkout" | "guests" | "rooms"
  >(null);

  // Refs for dropdown containers
  const checkInRef = useRef<HTMLDivElement>(null);
  const checkOutRef = useRef<HTMLDivElement>(null);
  const guestsRef = useRef<HTMLDivElement>(null);
  const roomsRef = useRef<HTMLDivElement>(null);

  // Debounced autocomplete fetch
  useEffect(() => {
    if (placeQuery.length < 3) {
      setPlaceSuggestions([]);
      setLoadingSuggestions(false);
      setShowSuggestions(false);
      return;
    }

    setLoadingSuggestions(true);
    setShowSuggestions(true);

    // Cancel previous request if any
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    const controller = new AbortController();
    abortControllerRef.current = controller;

    const timeoutId = setTimeout(() => {
      fetch(
        `/geo-autocomplete?query=${encodeURIComponent(
          placeQuery,
        )}&category=CITY&limit=5`,
        { signal: controller.signal },
      )
        .then((res) => {
          if (!res.ok) {
            setPlaceSuggestions([]);
            setLoadingSuggestions(false);
            return null;
          }
          return res.json();
        })
        .then((data) => {
          setPlaceSuggestions(data?.locations || []);
          setLoadingSuggestions(false);
        })
        .catch((err) => {
          if (err.name !== "AbortError") {
            setPlaceSuggestions([]);
            setLoadingSuggestions(false);
          }
        });
    }, 300); // 300ms debounce

    return () => {
      clearTimeout(timeoutId);
      controller.abort();
    };
  }, [placeQuery]);

  // Hide suggestions when input loses focus
  useEffect(() => {
    const handleBlur = (e: FocusEvent) => {
      setTimeout(() => setShowSuggestions(false), 100); // Delay to allow click
    };
    const input = inputRef.current;
    if (input) input.addEventListener("blur", handleBlur);
    return () => {
      if (input) input.removeEventListener("blur", handleBlur);
    };
  }, []);

  // Close dropdowns when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        !checkInRef.current?.contains(event.target as Node) &&
        !checkOutRef.current?.contains(event.target as Node) &&
        !guestsRef.current?.contains(event.target as Node) &&
        !roomsRef.current?.contains(event.target as Node)
      ) {
        setOpenDropdown(null);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Validate child ages before search
  const validateChildAges = () => {
    if (!childAges) return true;
    const ages = childAges
      .split(",")
      .map((age) => parseInt(age.trim(), 10))
      .filter((age) => !isNaN(age));
    if (ages.length !== children) {
      setChildAgeError("Please enter ages for all children.");
      return false;
    }
    if (ages.some((age) => age > MAX_CHILD_AGE)) {
      setChildAgeError("Max child age is 17. Please correct the ages.");
      return false;
    }
    setChildAgeError(null);
    return true;
  };

  const handleSearch = async () => {
    if (!validateChildAges()) return;
    setLoading(true);
    try {
      let latitude: number;
      let longitude: number;
      let countryCode: string;

      if (selectedPlace) {
        latitude = Number(selectedPlace.latitude);
        longitude = Number(selectedPlace.longitude);
        countryCode = selectedPlace.country || "AE";
      } else {
        // fallback to selected city
        const cityCoords: Record<
          string,
          { lat: number; lng: number; country: string }
        > = {
          GOA: { lat: 15.2993, lng: 74.124, country: "IN" },
          DXB: { lat: 25.276987, lng: 55.296249, country: "AE" },
          DEL: { lat: 28.6139, lng: 77.209, country: "IN" },
          BOM: { lat: 19.076, lng: 72.8777, country: "IN" },
          IST: { lat: 41.0082, lng: 28.9784, country: "TR" },
          LON: { lat: 51.5074, lng: -0.1278, country: "GB" },
        };
        const city = cityCoords[selectedCity.code] || cityCoords["DXB"];
        latitude = city.lat;
        longitude = city.lng;
        countryCode = city.country;
      }

      const payload = {
        latitude,
        longitude,
        countryCode,
        startDate: checkInDate,
        endDate: checkOutDate,
        adults,
        children,
        childAges,
        currency: currency || "AED",
        hotelName: "",
        offset: 1,
        pageSize: 40,
        radius: 200,
        rooms,
      };
      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/hotels/search`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        },
      );
      const data = await res.json();
      navigate("/hotel-results", {
        state: {
          city: selectedCity.code,
          checkInDate,
          checkOutDate,
          rooms,
          guests: adults + children,
          latitude: payload.latitude,
          longitude: payload.longitude,
          countryCode: payload.countryCode,
          currency: currency || "AED",
          results: data?.GetHotelAvailRS?.HotelAvailInfos?.HotelAvailInfo || [],
        },
      });
    } catch (err) {
      setLoading(false);
    }
    setLoading(false);
  };

  return (
    <div className="bg-primary text-black rounded-xl p-5 md:p-7 shadow-md text-sm text-left space-y-2">
      <div className="flex flex-wrap gap-3 md:gap-4 items-center justify-between tracking-tightest">
        {/* Destination */}
        <div className="relative">
          <div className="bg-white rounded-lg px-4 py-2 min-w-[180px] flex flex-col cursor-pointer">
            <p className="flex items-center justify-between text-subheading font-sora">
              Destination
              <FaMapMarkerAlt className="text-primary w-[21px] h-[21px] ml-2" />
            </p>
            <input
              ref={inputRef}
              type="text"
              className="bg-transparent outline-none text-superbase font-sora w-full mt-1"
              value={placeQuery}
              onChange={(e) => {
                setPlaceQuery(e.target.value);
                setSelectedPlace(null);
              }}
              placeholder="Enter city or location"
              autoComplete="off"
            />
            {showSuggestions && placeQuery.length >= 3 && (
              <div className="absolute z-20 bg-white rounded shadow mt-2 w-full max-h-60 overflow-y-auto">
                {loadingSuggestions && (
                  <div className="p-2 text-xs text-gray-500">Loading...</div>
                )}
                {!loadingSuggestions && placeSuggestions.length === 0 && (
                  <div className="p-2 text-xs text-gray-500">
                    No suggestions found
                  </div>
                )}
                {placeSuggestions.map((suggestion, idx) => (
                  <div
                    key={idx}
                    className="p-2 cursor-pointer hover:bg-card"
                    onMouseDown={() => {
                      setSelectedPlace(suggestion);
                      setPlaceQuery(suggestion.name);
                      setShowSuggestions(false);
                    }}
                  >
                    <span className="font-semibold">{suggestion.name}</span>
                    <span className="ml-2 text-xs text-gray-500">
                      {suggestion.countryName || suggestion.country}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Check-in */}
        <div className="relative" ref={checkInRef}>
          <div
            className="bg-white rounded-lg px-4 py-2 min-w-[180px] flex flex-col cursor-pointer"
            onClick={() =>
              setOpenDropdown(openDropdown === "checkin" ? null : "checkin")
            }
          >
            <p className="flex items-center justify-between text-subheading font-sora">
              Check In
              <FaCalendarAlt className="text-primary w-[21px] h-[21px] ml-2" />
            </p>
            <span className="text-superbase text-black font-normal mt-1">
              {checkInDate
                ? new Date(checkInDate)
                    .toLocaleDateString("en-GB", {
                      day: "2-digit",
                      month: "short",
                    })
                    .toUpperCase()
                : "SELECT DATE"}
            </span>
          </div>
          {openDropdown === "checkin" && (
            <CalendarDropdown
              months={1}
              selected={checkInDate}
              onSelect={(date) => {
                setCheckInDate(date);
                setOpenDropdown(null);
              }}
            />
          )}
        </div>

        {/* Check-out */}
        <div className="relative" ref={checkOutRef}>
          <div
            className="bg-white rounded-lg px-4 py-2 min-w-[180px] flex flex-col cursor-pointer"
            onClick={() =>
              setOpenDropdown(openDropdown === "checkout" ? null : "checkout")
            }
          >
            <p className="flex items-center justify-between text-subheading font-sora">
              Checkout
              <FaCalendarAlt className="text-primary w-[21px] h-[21px] ml-2" />
            </p>
            <span className="text-superbase text-black font-normal mt-1">
              {checkOutDate
                ? new Date(checkOutDate)
                    .toLocaleDateString("en-GB", {
                      day: "2-digit",
                      month: "short",
                    })
                    .toUpperCase()
                : "SELECT DATE"}
            </span>
          </div>
          {openDropdown === "checkout" && (
            <CalendarDropdown
              months={1}
              selected={checkOutDate}
              onSelect={(date) => {
                setCheckOutDate(date);
                setOpenDropdown(null);
              }}
            />
          )}
        </div>

        {/* Guests (Adults & Children) */}
        <div className="relative" ref={guestsRef}>
          <div
            className="bg-white rounded-lg px-4 py-2 min-w-[180px] flex flex-col cursor-pointer"
            onClick={() =>
              setOpenDropdown(openDropdown === "guests" ? null : "guests")
            }
          >
            <p className="flex items-center justify-between text-subheading font-sora">
              Guests
              <FaUserFriends className="text-primary w-[21px] h-[21px] ml-2" />
            </p>
            <span className="text-superbase text-black font-normal mt-1">
              {adults} Adult{adults > 1 ? "s" : ""}, {children} Child
              {children > 1 ? "ren" : ""}
            </span>
          </div>
          {openDropdown === "guests" && (
            <div className="absolute mt-2 bg-white rounded-lg p-4 space-y-3 shadow font-sans text-sm z-10 w-full min-w-[220px]">
              <div className="flex justify-between items-center border-b pb-2">
                <span className="font-bold text-black">Guests per Room</span>
                <FaUserFriends className="text-primary" />
              </div>
              <div className="flex justify-between items-center mt-2">
                <span>Adults</span>
                <div className="flex items-center gap-2">
                  <button
                    className="w-6 h-6 rounded bg-primary text-white text-xs"
                    onClick={() => setAdults(Math.max(adults - 1, 1))}
                  >
                    −
                  </button>
                  <span className="w-6 text-center">{adults}</span>
                  <button
                    className="w-6 h-6 rounded bg-primary text-white text-xs"
                    onClick={() => setAdults(adults + 1)}
                  >
                    +
                  </button>
                </div>
              </div>
              <div className="flex justify-between items-center mt-2">
                <span>Children</span>
                <div className="flex items-center gap-2">
                  <button
                    className="w-6 h-6 rounded bg-primary text-white text-xs"
                    onClick={() => setChildren(Math.max(children - 1, 0))}
                  >
                    −
                  </button>
                  <span className="w-6 text-center">{children}</span>
                  <button
                    className="w-6 h-6 rounded bg-primary text-white text-xs"
                    onClick={() => setChildren(children + 1)}
                  >
                    +
                  </button>
                </div>
              </div>
              {children > 0 && (
                <div className="mt-2">
                  <label className="block text-xs font-medium text-black mb-1">
                    Child Ages (comma separated, max age 17)
                  </label>
                  <input
                    type="text"
                    className="border rounded px-2 py-1 w-full"
                    value={childAges}
                    onChange={(e) => setChildAges(e.target.value)}
                    placeholder="e.g. 5, 10"
                  />
                  {childAgeError && (
                    <p className="text-xs text-red-600 mt-1">{childAgeError}</p>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Rooms */}
        <div className="relative" ref={roomsRef}>
          <div
            className="bg-white rounded-lg px-4 py-2 min-w-[180px] flex flex-col cursor-pointer"
            onClick={() =>
              setOpenDropdown(openDropdown === "rooms" ? null : "rooms")
            }
          >
            <p className="flex items-center justify-between text-subheading font-sora">
              Rooms
              <FaBed className="text-primary w-[21px] h-[21px] ml-2" />
            </p>
            <span className="text-superbase text-black font-normal mt-1">
              {rooms} Room{rooms > 1 ? "s" : ""}
            </span>
          </div>
          {openDropdown === "rooms" && (
            <div className="absolute mt-2 bg-white rounded-lg p-4 space-y-3 shadow font-sans text-sm z-10 w-full min-w-[200px]">
              <div className="flex justify-between items-center border-b pb-2">
                <span className="font-bold text-black">Rooms</span>
                <FaBed className="text-primary" />
              </div>
              <div className="flex justify-between items-center mt-2">
                <span>Rooms</span>
                <div className="flex items-center gap-2">
                  <button
                    className="w-6 h-6 rounded bg-primary text-white text-xs"
                    onClick={() => setRooms(Math.max(rooms - 1, 1))}
                  >
                    −
                  </button>
                  <span className="w-6 text-center">{rooms}</span>
                  <button
                    className="w-6 h-6 rounded bg-primary text-white text-xs"
                    onClick={() => setRooms(rooms + 1)}
                  >
                    +
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Bottom Section: Promo + Button */}
      <div className="flex flex-wrap items-center justify-between gap-4 mt-2">
        {/* <p className="text-xs text-white">
          Get Rs 500 Discount. Promo Code <strong>FIRSTBUILD</strong> for 1st
          time users.
        </p> */}
        <button
          onClick={handleSearch}
          className="bg-white text-black text-sm font-semibold px-6 py-2 rounded hover:bg-gray-100 transition flex items-center justify-center min-w-[140px]"
          disabled={loading}
        >
          {loading ? (
            <>
              <svg
                className="animate-spin h-5 w-5 mr-2 text-primary"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                ></circle>
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
                ></path>
              </svg>
              Loading...
            </>
          ) : (
            "Search Now"
          )}
        </button>
      </div>
    </div>
  );
};

export default HotelSearchbar;
